import { Component, ElementRef, Input, Output, EventEmitter, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { loadStripe, SetupIntent, Stripe, StripeElement, StripeElements } from '@stripe/stripe-js';
import { NgbModal, NgbActiveModal, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';

import { StripeService } from 'src/app/services/stripe.service';
import { AlertService } from 'src/app/services/alert.service';
import { EnvironmentService } from 'src/app/services/environment.service';
import { SetupIntentResponse } from 'src/app/models/io';
import { CommonModule, NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Location as LocationV2 } from 'src/app/models/location';
import { FacilityV2Service } from 'src/app/services/facilityV2.service';
import { MemberService } from 'src/app/services/member.service';
import { MemberModel } from 'src/app/models/member';

@Component({
  selector: 'df-add-payment-method',
  templateUrl: './add-payment-method.component.html',
  styleUrls: ['./add-payment-method.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, FormsModule, NgbModalModule, NgIf]
})
export class AddPaymentMethodComponent implements OnInit {
  @ViewChild('paymentElement') paymentElement!: ElementRef;
  private elements!: StripeElements;
  private paymentElementStripe!: StripeElement | null;

  @Input() public memberId : number = 0;
  @Output() passEntry: EventEmitter<boolean | SetupIntent | null> = new EventEmitter();
  submitted:boolean  = false;
  errorMessage: string = "";
  successMessage: string = "";
  makeDefault: boolean = false;
  
  stripe!: Stripe | null;
  isLoading = true;
  paymentDetailsAdded = false;
  connectedAccountName: string = '';
  member: MemberModel;
  location: LocationV2;
  setupIntent: SetupIntent | null = null;

  // Track selected payment method type so we can tailor micro-deposit UX.
  selectedPaymentMethodType: string = '';
  showCloseButton = false;

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private alertService: AlertService,
    private stripeService: StripeService,
    private environmentService: EnvironmentService,
    private facilityV2Service: FacilityV2Service,
    private memberService: MemberService
  ) {}

  async ngOnInit(): Promise<void> {
    // Load member first so we use the same location as the backend when creating the SetupIntent.
    // Using localStorage location here can cause "client_secret does not match any SetupIntent" when they differ.
    try {
      this.member = await this.memberService.getMember();
    } catch {
      this.member = {} as MemberModel;
    }

    const defaultLocation = { credit_card_surcharge: 0 } as LocationV2;
    const locationId = this.member && this.member.LocationId ? this.member.LocationId : null;
    if (locationId) {
      try {
        this.location = await this.facilityV2Service.getLocationById(locationId);
      } catch {
        this.location = defaultLocation;
      }
    } else {
      this.location = defaultLocation;
    }

    // Use member's location so frontend and backend use the same Stripe Connect account; fall back to default if no member location.
    const stripeAccountId = this.environmentService.getStripeAccountIdForLocation(this.member?.LocationId)
      || this.environmentService.getStripeAccountId();
    this.stripe = await loadStripe(
      this.environmentService.getFacilityConfig().stripe.publishedKey,
      stripeAccountId ? { stripeAccount: stripeAccountId } : undefined
    );

    if (this.stripe) {
      this.connectedAccountName = this.environmentService.getFacilityConfig().name || 'Drop Fitness';
      this.createSetupIntent();
    } else {
      this.isLoading = false;
    }
  }

  createSetupIntent() {
    this.stripeService.createSetupIntent(this.memberId).subscribe({
      next: (result: SetupIntentResponse) => {
        if (result.ClientSecret) {
          // Initialize elements with the setup intent client secret
          this.elements = this.stripe!.elements({
            clientSecret: result.ClientSecret,
          });
          this.createPaymentElement();
        } else {
          this.errorMessage = 'Failed to initialize payment form';
          this.isLoading = false;
        }
      },
      error: (error: any) => {
        console.error('Failed to create setup intent:', error);
        this.errorMessage = 'Failed to initialize payment form';
        this.isLoading = false;
      }
    });
  }

  allowsBankPaymentMethods(): boolean {
    return this.getSupportedPaymentMethods().includes('us_bank_account');
  }

  private getSupportedPaymentMethods(): string[] {
    const supportedPaymentMethods = this.environmentService.getFacilityConfig().stripe?.supportedPaymentMethods;
    return supportedPaymentMethods && supportedPaymentMethods.length > 0 ? supportedPaymentMethods : ['card'];
  }

  private getPaymentElementOptions(): any {
    const supportedPaymentMethods = this.getSupportedPaymentMethods();
    const allowBankPaymentMethods = this.allowsBankPaymentMethods();
    const options: any = {
      terms: {
        card: 'always',
        usBankAccount: allowBankPaymentMethods ? 'auto' : 'never'
      },
      fields: {
        billingDetails: {
          name: 'auto',
        }
      },
      layout: {
        type: 'accordion',
        defaultCollapsed: allowBankPaymentMethods,
        radios: allowBankPaymentMethods ? 'always' : 'never'
      },
      paymentMethodOrder: supportedPaymentMethods,
      business: {
        name: this.connectedAccountName
      },
      wallets: {
        applePay: 'never',
        googlePay: 'never',
        link: 'never'
      }
    };

    return options;
  }

  createPaymentElement() {
    try {
      this.paymentElementStripe = this.elements.create('payment', this.getPaymentElementOptions());
      
      this.paymentElementStripe.mount('#payment-element');

      this.paymentElementStripe.on('change', (event: any) => {
        this.paymentDetailsAdded = event.complete;
        // Stripe Payment Element provides the selected type in event.value.type
        this.selectedPaymentMethodType = (event?.value?.type || '').toString();
        if (event.error) {
          this.errorMessage = event.error.message;
        }
      });

      this.paymentElementStripe.on('loaderror', (event: { error: { message?: string } }) => {
        this.isLoading = false;
        this.paymentDetailsAdded = false;
        if (this.paymentElementStripe) {
          this.paymentElementStripe.unmount();
          this.paymentElementStripe = null;
        }
        const msg = event?.error?.message;
        const isHttp = typeof window !== 'undefined' && window.location?.protocol === 'http:';
        this.errorMessage = msg
          ? msg
          : isHttp
            ? 'Payment form could not load. Try using HTTPS (e.g. ngrok or a staging URL) or check your Stripe configuration.'
            : 'Payment form could not load. Please try again or use a different payment method.';
      });

      this.isLoading = false;
    } catch (error) {
      console.error('Payment Element initialization failed:', error);
      this.paymentElementStripe = null;
      this.isLoading = false;
    }
  }

  async onSubmit(): Promise<void> {
    if (!this.stripe || !this.paymentElementStripe) {
      this.errorMessage = 'Payment system not initialized.';
      return;
    }

    this.submitted = true;
    this.errorMessage = "";
    this.successMessage = "";

    try {
      // Confirm the setup intent to add the payment method
      const { error, setupIntent } = await this.stripe.confirmSetup({
        elements: this.elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (error) {
        this.errorMessage = error.message || 'Failed to add payment method';
        this.submitted = false;
      } else if (setupIntent && (
        setupIntent.status === 'succeeded'
      )) {
        // Payment method was successfully added
        this.setupIntent = setupIntent;
        // if (this.makeDefault && setupIntent.payment_method) {
        //   await this.memberService.setDefaultPaymentMethod(this.memberId, setupIntent.payment_method).toPromise();
        // }
        this.successMessage = 'Payment method successfully added!';
        this.submitted = false;
        setTimeout(() => {
          this.closeModal(true);
        }, 1500);
      } else if (setupIntent && (
        setupIntent.status === 'requires_confirmation' || setupIntent.status === 'requires_action' || setupIntent.status === 'processing'
      )) {
        // Bank micro-deposits flows can land here. Do NOT auto-close; allow user to read instructions.
        this.setupIntent = setupIntent;

        const isUsBank = (this.selectedPaymentMethodType || '').toLowerCase() === 'us_bank_account';
        if (isUsBank) {
          // Track the SetupIntent + PaymentMethod so the backend can surface a "pending verification" bank method.
          const setupIntentId = (setupIntent as any)?.id;
          const paymentMethodId = (setupIntent as any)?.payment_method;
          if (setupIntentId && paymentMethodId) {
            this.stripeService.trackSetupIntent(setupIntentId, paymentMethodId).subscribe({
              error: () => {
                // best-effort only; do not block UX on tracking
              }
            });
          }

          this.successMessage = 'Payment method will become available after confirmation. A micro-deposit will be made into your bank account in a few days. Come back to verify once deposited, or follow verification instructions sent in the email.';
          this.showCloseButton = true;
        } else {
          // Fallback for other payment method types
          this.successMessage = 'Payment method will become available after confirmation.';
          setTimeout(() => {
            this.closeModal(true);
          }, 1500);
        }

        this.submitted = false;
      } else {
        this.errorMessage = 'Payment method setup was not completed';
        this.submitted = false;
      }
    } catch (err) {
      this.errorMessage = 'An unexpected error occurred.';
      this.submitted = false;
      console.error(err);
    }
  }

  onMakeDefaultChange(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    this.makeDefault = checkbox.checked;
  }

  closeModal(status:boolean | SetupIntent | null = false) {
    this.passEntry.emit(status);
    this.activeModal.close(status);
  }
}
